import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { MeetingMinutesReview } from '@/components/meetings/MeetingMinutesReview';

interface ConductMeetingPhase3Props {
  taskId: string;
  unaId: string;
  task: any;
  phaseData: any;
  onPhaseComplete?: () => void;
}

/**
 * Phase 3: Finalize Minutes
 * Wrapper for MeetingMinutesReview - review recorded minutes, generate formal document, collect signatures
 */
export function ConductMeetingPhase3({ taskId, unaId, task, phaseData, onPhaseComplete }: ConductMeetingPhase3Props) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [meetingMinutesId, setMeetingMinutesId] = useState<string | null>(null);

  useEffect(() => {
    loadMeetingRecord();
  }, [taskId]);

  async function loadMeetingRecord() {
    try {
      setLoading(true);
      
      // Find the meeting_minutes record for this task
      const { data: minutes, error } = await supabase
        .from('meeting_minutes')
        .select('id, status')
        .eq('task_id', taskId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching meeting minutes:', error);
        toast({
          title: 'Error',
          description: 'Could not load meeting record',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      if (!minutes) {
        console.warn('No meeting_minutes found for task:', taskId);
        toast({
          title: 'Meeting Record Not Found',
          description: 'Please complete Phase 2 first to record the meeting.',
          variant: 'destructive',
        });
        setLoading(false);
        return;
      }

      console.log('✅ Found meeting_minutes:', minutes.id, 'status:', minutes.status);
      setMeetingMinutesId(minutes.id);
    } catch (error) {
      console.error('Error in loadMeetingRecord:', error);
      toast({
        title: 'Error',
        description: 'Failed to load meeting data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8 space-y-2">
        <Loader2 className="h-8 w-8 animate-spin" />
        <p className="text-sm text-muted-foreground">Loading meeting minutes review...</p>
      </div>
    );
  }

  if (!meetingMinutesId) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-center text-muted-foreground">
            No meeting record found. Please complete Phase 2 first to record the meeting.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Phase Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <CardTitle>Phase 3: Finalize Minutes</CardTitle>
                <Badge>Current Phase</Badge>
              </div>
              <p className="text-muted-foreground">
                Review recorded minutes, generate formal document, and collect required signatures
              </p>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Meeting Minutes Review */}
      <MeetingMinutesReview
        meetingId={meetingMinutesId}
        unaId={unaId}
        taskId={taskId}
        onPhaseComplete={onPhaseComplete}
      />
    </div>
  );
}
